import { useEffect, useRef } from "react";
import * as Tone from "tone";
import { SampleKit } from "./useSamples";

interface DrumPattern {
  kick:  number[];
  snare: number[];
  hat:   number[];
}

// 16-step (16th-note) boom-bap probabilities per instrument.
// Index 0 = beat 1, 4 = beat 2, 8 = beat 3, 12 = beat 4.
const PATTERNS: DrumPattern[] = [
  // Classic boom-bap
  {
    kick:  [1,   0, 0,   0,    0,   0, 0.6, 0,  0,   0, 0,   0.3,  0,   0, 0.2, 0],
    snare: [0,   0, 0,   0,    1,   0, 0,   0,  0,   0, 0,   0,    1,   0, 0,   0],
    hat:   [0.8, 0, 0.8, 0,    0.8, 0, 0.8, 0,  0.8, 0, 0.5, 0,    0.8, 0, 0.5, 0],
  },
  // Half-time / laid-back — snare only on beat 3, sparser hats
  {
    kick:  [1,   0, 0,   0.3,  0,   0, 0,   0,  1,   0, 0,   0,    0,   0, 0.4, 0],
    snare: [0,   0, 0,   0,    0,   0, 0,   0,  1,   0, 0,   0,    0,   0, 0,   0],
    hat:   [0.7, 0, 0,   0,    0.7, 0, 0.5, 0,  0.7, 0, 0,   0,    0.7, 0, 0.5, 0],
  },
  // Syncopated — busier kick, ghost snares, busier hats
  {
    kick:  [1,   0, 0.3, 0,    0,   0, 0.7, 0,  0.5, 0, 0,   0.4,  0,   0, 0.5, 0],
    snare: [0,   0, 0,   0.2,  1,   0, 0,   0,  0,   0, 0,   0.2,  1,   0, 0,   0],
    hat:   [0.9, 0, 0.9, 0.5,  0.9, 0, 0.9, 0.5, 0.9, 0, 0.9, 0.5, 0.9, 0, 0.9, 0.5],
  },
  // Sparse / minimal — kick on 1 and 3, snare on 4, very few hats
  {
    kick:  [1,   0, 0,   0,    0,   0, 0,   0,  0.7, 0, 0,   0,    0,   0, 0.3, 0],
    snare: [0,   0, 0,   0,    0,   0, 0,   0,  0,   0, 0,   0,    1,   0, 0,   0],
    hat:   [0.6, 0, 0,   0,    0.6, 0, 0.4, 0,  0.6, 0, 0,   0,    0.6, 0, 0.4, 0],
  },
];

const HUMANIZE_S = 0.015;

function jitter() {
  return (Math.random() - 0.5) * HUMANIZE_S;
}

export interface UseDrumsResult {
  switchPattern: () => void;
}

export function useDrums(
  samples: SampleKit | null,
  configRef: { current: { enabled: boolean; hihatEnabled: boolean } },
): UseDrumsResult {
  const stepRef = useRef(0);
  const patternIndexRef = useRef(0);

  useEffect(() => {
    if (!samples) return;

    const id = Tone.Transport.scheduleRepeat((time) => {
      if (!configRef.current.enabled) { stepRef.current = (stepRef.current + 1) % 16; return; }
      const step = stepRef.current;
      const { kick, snare, hat } = PATTERNS[patternIndexRef.current];

      if (Math.random() < kick[step])
        samples.drums.player("kick").start(time + jitter());
      if (Math.random() < snare[step])
        samples.drums.player("snare").start(time + jitter());
      if (configRef.current.hihatEnabled && Math.random() < hat[step])
        samples.drums.player("hihat").start(time + jitter());

      stepRef.current = (step + 1) % 16;
    }, "16n");

    return () => {
      Tone.Transport.clear(id);
      stepRef.current = 0;
    };
  }, [samples, configRef]);

  function switchPattern() {
    const current = patternIndexRef.current;
    let next: number;
    do { next = Math.floor(Math.random() * PATTERNS.length); } while (next === current && PATTERNS.length > 1);
    patternIndexRef.current = next;
    stepRef.current = 0;
  }

  return { switchPattern };
}
